import React from "react";
import Link from "next/link";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Meta from "../Meta";
import Loader from "../loader";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { applyLang } from "../../../i18n";
import renderIcon from "../../../components/renderIcon";
import { ASSET_URL } from "../../../config";
import StarRating from "../components/StarRating";
import MenuGallery from "../components/MenuGallery";
import { walkMinutesFrom, getBethHabadLabel } from "../utils";

// Extrait l'ID d'une vidéo YouTube depuis une URL (watch?v=, youtu.be/, embed/, shorts/).
function youtubeId(url) {
    if (!url) return null;
    const m = String(url).match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
    return m ? m[1] : null;
}

const SOCIAL_ICONS = {
    facebook: { name: "IconBrandFacebook", source: "tabler" },
    instagram: { name: "IconBrandInstagram", source: "tabler" },
    youtube: { name: "IconBrandYoutube", source: "tabler" },
    tiktok: { name: "IconBrandTiktok", source: "tabler" },
};

const detailStyles = `
.tr-cdetail__hero { max-width: 1180px; margin: 24px auto 32px; padding: 0 24px; box-sizing: border-box; width: 100%; }
.tr-cdetail__hero-img {
  width: 100%; aspect-ratio: 16/7; object-fit: cover; border-radius: 32px; display: block; background: #e5e7eb;
}
.tr-cdetail__hero-placeholder {
  width: 100%; aspect-ratio: 16/7; border-radius: 32px;
  background: linear-gradient(135deg, #e5e7eb, #cbd5e1);
  display: flex; align-items: center; justify-content: center; color: #6b7280;
}
.tr-cdetail__body { max-width: 1180px; margin: 0 auto; padding: 0 24px; box-sizing: border-box; width: 100%; }
.tr-cdetail__title {
  font-size: clamp(26px, 4vw, 40px); font-weight: 800; color: #1a1a2e; margin: 0 0 12px; line-height: 1.2;
}
.tr-cdetail__rating-row { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin-bottom: 20px; }
.tr-cdetail__walk {
  display: inline-flex; align-items: center; gap: 6px; font-size: 14px; font-weight: 600; color: var(--primary, #1b579e);
}
.tr-cdetail__grid { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 380px); gap: 40px; align-items: start; }
.tr-cdetail__grid > * { min-width: 0; }
@media (max-width: 900px) { .tr-cdetail__grid { grid-template-columns: 1fr; } }
.tr-cdetail__tags { display: flex; flex-wrap: wrap; gap: 10px; margin-bottom: 18px; }
.tr-cdetail__tag {
  display: inline-block; font-size: 13px; font-weight: 500; padding: 6px 14px; border-radius: 999px; background: #ffe8d9; color: #1a1a2e;
}
.tr-cdetail__info { font-size: 14px; color: #374151; margin-bottom: 8px; line-height: 1.5; }
.tr-cdetail__info b { color: #1a1a2e; font-weight: 700; margin-inline-end: 4px; }
.tr-cdetail__actions { display: flex; flex-wrap: wrap; gap: 20px; margin-top: 24px; }
.tr-cdetail__action {
  display: flex; flex-direction: column; align-items: center; gap: 6px; text-decoration: none; color: #1a1a2e; font-size: 12px; font-weight: 500;
}
.tr-cdetail__action-btn {
  width: 56px; height: 56px; border-radius: 16px;
  background: color-mix(in srgb, var(--primary, #1b579e) 14%, white);
  display: flex; align-items: center; justify-content: center; color: var(--primary, #1b579e);
  transition: transform .15s, box-shadow .15s;
}
.tr-cdetail__action:hover .tr-cdetail__action-btn {
  transform: translateY(-2px); box-shadow: 0 6px 14px color-mix(in srgb, var(--primary, #1b579e) 25%, transparent);
}
.tr-cdetail__hours-title { font-size: 16px; font-weight: 700; color: #1a1a2e; margin: 40px 0 14px; }
.tr-cdetail__hours {
  display: grid; grid-template-columns: 110px minmax(0, 1fr); row-gap: 10px; column-gap: 12px; font-size: 14px; color: #374151; max-width: 100%; width: 420px;
}
@media (max-width: 480px) { .tr-cdetail__hours { width: 100%; } }
.tr-cdetail__hours-day { color: #6b7280; text-transform: lowercase; }
.tr-cdetail__hours-slots { display: flex; flex-direction: column; gap: 2px; color: #1a1a2e; }
.tr-cdetail__hours-closed { color: #9ca3af; font-style: italic; }
.tr-cdetail__desc { margin-top: 18px; font-size: 15px; line-height: 1.7; color: #1f2937; max-width: 720px; overflow-wrap: anywhere; }
.tr-cdetail__desc img { max-width: 100%; height: auto; }
.tr-cdetail__desc h1, .tr-cdetail__desc h2, .tr-cdetail__desc h3 { color: #1a1a2e; margin-top: 24px; margin-bottom: 10px; }
.tr-cdetail__desc p { margin: 0 0 12px; }
.tr-cdetail__desc a { color: var(--primary, #1b579e); text-decoration: underline; }
.tr-cdetail__side {
  background: white; border-radius: 24px; padding: 22px 24px;
  box-shadow: 0 8px 24px rgba(0,0,0,.06), 0 1px 4px rgba(0,0,0,.04);
  display: flex; flex-direction: column; gap: 16px; position: sticky; top: 88px;
  max-width: 100%; box-sizing: border-box;
}
.tr-cdetail__side-row { display: flex; align-items: center; gap: 12px; text-decoration: none; color: #374151; font-size: 14px; line-height: 1.4; min-width: 0; }
.tr-cdetail__side-row span:last-child { min-width: 0; overflow-wrap: anywhere; }
.tr-cdetail__side-row:hover { color: #1a1a2e; }
.tr-cdetail__side-icon {
  width: 40px; height: 40px; border-radius: 12px; display: flex; align-items: center; justify-content: center; flex-shrink: 0;
  background: color-mix(in srgb, var(--primary, #1b579e) 14%, white); color: var(--primary, #1b579e);
}
.tr-cdetail__back {
  display: inline-flex; align-items: center; gap: 6px; color: var(--primary, #1b579e); font-size: 14px; font-weight: 500; margin-bottom: 18px; text-decoration: none;
}
.tr-cdetail__back:hover { text-decoration: underline; }
.tr-cdetail__empty { text-align: center; padding: 80px 24px; }
.tr-cdetail__subtitle { font-size: 16px; color: #4b5563; margin: 0 0 16px; line-height: 1.4; }
.tr-cdetail__tag--badge { background: #fef3c7; color: #92400e; font-weight: 700; }
.tr-cdetail__video {
  margin-top: 32px; max-width: 720px; aspect-ratio: 16/9; border-radius: 16px; overflow: hidden;
  box-shadow: 0 8px 24px rgba(0,0,0,.12);
}
.tr-cdetail__video iframe { width: 100%; height: 100%; border: 0; display: block; }
.tr-cdetail__socials { display: flex; gap: 12px; margin-top: 28px; }
.tr-cdetail__social {
  width: 44px; height: 44px; border-radius: 12px; display: flex; align-items: center; justify-content: center;
  background: color-mix(in srgb, var(--primary, #1b579e) 12%, white); color: var(--primary, #1b579e);
  transition: transform .15s, box-shadow .15s; text-decoration: none;
}
.tr-cdetail__social:hover { transform: translateY(-2px); box-shadow: 0 6px 14px color-mix(in srgb, var(--primary, #1b579e) 22%, transparent); }
`;

const DAYS_ORDER = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function ActionButton({ type, href, label, icon, external }) {
    return (
        <a
            href={href}
            className={`tr-cdetail__action tr-cdetail__action--${type}`}
            target={external ? "_blank" : undefined}
            rel={external ? "noopener noreferrer" : undefined}
        >
            <span className="tr-cdetail__action-btn">{renderIcon(icon, 26)}</span>
            <span>{label}</span>
        </a>
    );
}

export default function CustomSectionDetail({ context, loading }) {
    const { org, travel } = context;

    const slug = context.route?.[2];
    const itemId = context.route?.[3];
    const rawSection = (travel?.sections || []).find(s => s.slug === slug);
    const rawItem = (travel?.sectionItems || []).find(i => i.id === itemId && i.sectionId === rawSection?.id);

    const DAY_LABELS = {
        sunday: t`dimanche`, monday: t`lundi`, tuesday: t`mardi`, wednesday: t`mercredi`,
        thursday: t`jeudi`, friday: t`vendredi`, saturday: t`samedi`,
    };
    const bethHabadLabel = getBethHabadLabel(travel, context.lang);

    if (!rawSection || !rawItem) {
        return (
            <div className="travel-page travel-page--custom-section-detail">
                <style dangerouslySetInnerHTML={{ __html: detailStyles }} />
                <Meta context={context} title={org.name} description={org.name} />
                {loading && <Loader />}
                <Header context={context} />
                <main className="travel-main">
                    <div className="tr-cdetail__empty">
                        <h1><Trans>Élément introuvable</Trans></h1>
                        <Link href={org.BASE_WEB_URL + (rawSection ? "/travel/c/" + rawSection.slug : "")} legacyBehavior>
                            <a className="tr-cdetail__back">
                                {renderIcon({ name: "IconArrowLeft", source: "tabler" }, 16)}
                                <Trans>Retour</Trans>
                            </a>
                        </Link>
                    </div>
                </main>
                <Footer context={context} />
            </div>
        );
    }

    const fields = rawSection.fields || {};
    const item = applyLang(rawItem, context.lang);
    const categories = (rawSection.categories || []).map(c => applyLang(c, context.lang));
    const category = categories.find(c => c.id === item.categoryId) || null;

    const imgSrc = item.image ? `${ASSET_URL}${item.image}` : null;
    const address = item.address?.address;
    const lat = item.address?.lat;
    const lon = item.address?.lon;
    const navUrl = lat != null && lon != null
        ? `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`
        : (address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : null);

    const walkMinutes = walkMinutesFrom(org, item);

    const contacts = item.contacts || [];
    const phoneContact = contacts.find(c => c.type === "phone");
    const emailContact = contacts.find(c => c.type === "email");
    const whatsappContact = contacts.find(c => c.type === "whatsapp");

    const stripHtml = (s) => (s || "").replace(/<[^>]*>/g, "").trim();
    const placeholderIcon = rawSection.icon?.name ? rawSection.icon : { name: "IconMapPin", source: "tabler" };
    const itemDesc = stripHtml(item.description) || item.name;

    const DAY_MAP = { sunday: "Sunday", monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday", friday: "Friday", saturday: "Saturday" };
    const openingHours = [];
    if (fields.hours && item.hours) {
        for (const [day, slots] of Object.entries(item.hours)) {
            if (!Array.isArray(slots)) continue;
            for (const s of slots) {
                if (!s.open || !s.close) continue;
                openingHours.push({ "@type": "OpeningHoursSpecification", dayOfWeek: DAY_MAP[day] || day, opens: s.open, closes: s.close });
            }
        }
    }

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "Place",
        name: item.name,
        description: itemDesc,
        image: imgSrc || undefined,
        address: address ? { "@type": "PostalAddress", streetAddress: address } : undefined,
        geo: (lat != null && lon != null) ? { "@type": "GeoCoordinates", latitude: lat, longitude: lon } : undefined,
        telephone: phoneContact?.value,
        email: emailContact?.value,
        url: (fields.website && item.website) || undefined,
        aggregateRating: (fields.note && item.note) ? {
            "@type": "AggregateRating", ratingValue: item.note, bestRating: 5, ratingCount: 1,
        } : undefined,
        openingHoursSpecification: openingHours.length > 0 ? openingHours : undefined,
    };

    return (
        <div className="travel-page travel-page--custom-section-detail tr-cdetail">
            <style dangerouslySetInnerHTML={{ __html: detailStyles }} />
            <Meta
                context={context}
                title={item.name}
                description={itemDesc.slice(0, 200)}
                image={imgSrc}
                jsonLd={jsonLd}
            />

            {loading && <Loader />}
            <Header context={context} />

            <main className="travel-main">

                <div className="tr-cdetail__hero">
                    {imgSrc ? (
                        <img src={imgSrc} alt={item.name} className="tr-cdetail__hero-img" />
                    ) : (
                        <div className="tr-cdetail__hero-placeholder">
                            {renderIcon(placeholderIcon, 64)}
                        </div>
                    )}
                </div>

                <div className="tr-cdetail__body">

                    <Link href={org.BASE_WEB_URL + "/travel/c/" + rawSection.slug} legacyBehavior>
                        <a className="tr-cdetail__back">
                            {renderIcon({ name: "IconArrowLeft", source: "tabler" }, 16)}
                            <Trans>Retour</Trans>
                        </a>
                    </Link>

                    <h1 className="tr-cdetail__title">{item.name}</h1>

                    {fields.subtitle && item.subtitle && (
                        <div className="tr-cdetail__subtitle">{item.subtitle}</div>
                    )}

                    {((fields.note && item.note) || walkMinutes != null) && (
                        <div className="tr-cdetail__rating-row">
                            {fields.note && <StarRating value={item.note} size={20} showValue />}
                            {walkMinutes != null && (
                                <span className="tr-cdetail__walk">
                                    {renderIcon({ name: "IconWalk", source: "tabler" }, 18)}
                                    <Trans>{walkMinutes} min du {bethHabadLabel}</Trans>
                                </span>
                            )}
                        </div>
                    )}

                    <div className="tr-cdetail__grid">

                        <div>
                            <div className="tr-cdetail__tags">
                                {category?.label && (
                                    <span className="tr-cdetail__tag">{category.label}</span>
                                )}
                                {fields.badge && item.badge && (
                                    <span className="tr-cdetail__tag tr-cdetail__tag--badge">{item.badge}</span>
                                )}
                            </div>

                            {fields.price && item.price && (
                                <div className="tr-cdetail__info">
                                    <b><Trans>Tarif :</Trans></b>{item.price}
                                </div>
                            )}

                            {item.description && (
                                <div className="tr-cdetail__desc" dangerouslySetInnerHTML={{ __html: item.description }} />
                            )}

                            <div className="tr-cdetail__actions">
                                {phoneContact && (
                                    <ActionButton type="phone" href={`tel:${phoneContact.value}`} label={t`Appel`} icon={{ name: "IconPhone", source: "tabler" }} />
                                )}
                                {navUrl && (
                                    <ActionButton type="nav" href={navUrl} label={t`Naviguer`} icon={{ name: "IconMapPin", source: "tabler" }} external />
                                )}
                                {whatsappContact && (
                                    <ActionButton type="whatsapp" href={`https://wa.me/${whatsappContact.value.replace(/\D/g, "")}`} label="WhatsApp" icon={{ name: "IconBrandWhatsapp", source: "tabler" }} external />
                                )}
                                {emailContact && (
                                    <ActionButton type="email" href={`mailto:${emailContact.value}`} label={t`E-mail`} icon={{ name: "IconMail", source: "tabler" }} />
                                )}
                                {fields.website && item.website && (
                                    <ActionButton type="website" href={item.website} label={t`Site web`} icon={{ name: "IconWorld", source: "tabler" }} external />
                                )}
                                {fields.actionButton && item.actionUrl && (
                                    <ActionButton type="cta" href={item.actionUrl} label={item.actionLabel || t`En savoir plus`} icon={{ name: "IconHandClick", source: "tabler" }} external />
                                )}
                            </div>

                            {fields.hours && item.hours && Object.values(item.hours).some(slots => slots?.length > 0) && (
                                <>
                                    <h3 className="tr-cdetail__hours-title"><Trans>Horaires d'ouverture</Trans></h3>
                                    <div className="tr-cdetail__hours">
                                        {DAYS_ORDER.map(day => {
                                            const slots = item.hours[day] || [];
                                            return (
                                                <React.Fragment key={day}>
                                                    <div className="tr-cdetail__hours-day">{DAY_LABELS[day]}</div>
                                                    <div className="tr-cdetail__hours-slots">
                                                        {slots.length === 0 ? (
                                                            <span className="tr-cdetail__hours-closed"><Trans>Fermé</Trans></span>
                                                        ) : (
                                                            slots.map((s, i) => <span key={i}>{s.open}-{s.close}</span>)
                                                        )}
                                                    </div>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                </>
                            )}

                            {/* Vidéo YouTube */}
                            {fields.video && youtubeId(item.video) && (
                                <div className="tr-cdetail__video">
                                    <iframe
                                        src={`https://www.youtube.com/embed/${youtubeId(item.video)}`}
                                        title={item.name}
                                        loading="lazy"
                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                        allowFullScreen
                                    />
                                </div>
                            )}

                            {/* Galerie de photos */}
                            {fields.gallery && Array.isArray(item.images) && item.images.length > 0 && (
                                <>
                                    <h3 className="tr-cdetail__hours-title"><Trans>Photos</Trans></h3>
                                    <MenuGallery images={item.images} />
                                </>
                            )}

                            {/* PDF */}
                            {fields.pdf && item.pdf && (
                                <div style={{ marginTop: 24 }}>
                                    <a
                                        href={`${ASSET_URL}${item.pdf}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="tr-cdetail__action"
                                        style={{ flexDirection: "row", gap: 10 }}
                                    >
                                        <span className="tr-cdetail__action-btn">
                                            {renderIcon({ name: "IconFileText", source: "tabler" }, 24)}
                                        </span>
                                        <span><Trans>Télécharger le document</Trans></span>
                                    </a>
                                </div>
                            )}

                            {/* Réseaux sociaux */}
                            {fields.socials && item.socials && Object.values(item.socials).some(Boolean) && (
                                <div className="tr-cdetail__socials">
                                    {Object.entries(SOCIAL_ICONS).map(([key, icon]) => (
                                        item.socials[key] ? (
                                            <a key={key} href={item.socials[key]} target="_blank" rel="noopener noreferrer" className="tr-cdetail__social" aria-label={key}>
                                                {renderIcon(icon, 22)}
                                            </a>
                                        ) : null
                                    ))}
                                </div>
                            )}
                        </div>

                        <aside className="tr-cdetail__side">
                            {address && (
                                <a href={navUrl || "#"} className="tr-cdetail__side-row" target="_blank" rel="noopener noreferrer">
                                    <span className="tr-cdetail__side-icon">{renderIcon({ name: "IconMapPin", source: "tabler" }, 20)}</span>
                                    <span>{address}</span>
                                </a>
                            )}
                            {phoneContact && (
                                <a href={`tel:${phoneContact.value}`} className="tr-cdetail__side-row">
                                    <span className="tr-cdetail__side-icon">{renderIcon({ name: "IconPhone", source: "tabler" }, 20)}</span>
                                    <span>{phoneContact.value}</span>
                                </a>
                            )}
                            {whatsappContact && (
                                <a href={`https://wa.me/${whatsappContact.value.replace(/\D/g, "")}`} className="tr-cdetail__side-row" target="_blank" rel="noopener noreferrer">
                                    <span className="tr-cdetail__side-icon">{renderIcon({ name: "IconBrandWhatsapp", source: "tabler" }, 20)}</span>
                                    <span>{whatsappContact.value}</span>
                                </a>
                            )}
                            {emailContact && (
                                <a href={`mailto:${emailContact.value}`} className="tr-cdetail__side-row">
                                    <span className="tr-cdetail__side-icon">{renderIcon({ name: "IconMail", source: "tabler" }, 20)}</span>
                                    <span>{emailContact.value}</span>
                                </a>
                            )}
                            {fields.website && item.website && (
                                <a href={item.website} className="tr-cdetail__side-row" target="_blank" rel="noopener noreferrer">
                                    <span className="tr-cdetail__side-icon">{renderIcon({ name: "IconWorld", source: "tabler" }, 20)}</span>
                                    <span><Trans>Site web</Trans></span>
                                </a>
                            )}
                        </aside>

                    </div>

                </div>
            </main>

            <Footer context={context} />
        </div>
    );
}
